import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { HttpService } from '../http.service';
import { SubmitResponse } from '../../../models/common/api-response.model';
import { Observable } from 'rxjs';
import {
  Blog,
  BlogCategory,
  BlogCategoryListResponse,
  BlogCategoryPayload,
  BlogListResponse,
  BlogPayload,
  PublicBlog,
  PublicBlogCategory,
  PublicBlogListResponse,
} from '../../../models/admin/blogs.model';

@Injectable({
  providedIn: 'root'
})
export class BlogsService extends HttpService<any, BlogListResponse> {
  constructor(httpClient: HttpClient, @Inject('API_URL') apiUrl: string, @Inject('API_VERSION') apiVersion: string) {
    super(httpClient, apiUrl, apiVersion, 'blogs');
  }

  getBlogsList(
    page: number = 1,
    limit: number = 10,
    filters: { search?: string | null; isActive?: boolean | null; categoryId?: string | number | null } = {}
  ): Observable<BlogListResponse> {
    const hasSearch = !!filters.search;
    const hasStatus = filters.isActive !== null && filters.isActive !== undefined;
    const hasCategory = filters.categoryId !== null && filters.categoryId !== undefined && filters.categoryId !== '';

    const params: Record<string, string | number | boolean> = {};

    if (hasSearch) {
      params['search'] = filters.search as string;
    }
    if (hasStatus) {
      params['is_active'] = filters.isActive as boolean;
    }
    if (hasCategory) {
      params['blog_category_id'] = filters.categoryId as string | number;
    }
    if (!hasSearch && !hasStatus && !hasCategory) {
      params['page'] = page;
      params['limit'] = limit;
    }

    return this.getBy('', params) as unknown as Observable<BlogListResponse>;
  }

  addNewBlog(blog: BlogPayload): Observable<Blog> {
    return this.create(blog) as unknown as Observable<Blog>;
  }

  updateBlog(id: string | number, blog: BlogPayload): Observable<SubmitResponse> {
    return this.patch(id, blog) as unknown as Observable<SubmitResponse>;
  }

  deleteBlog(id: string | number): Observable<SubmitResponse> {
    return this.delete(id) as unknown as Observable<SubmitResponse>;
  }

  updateBlogStatus(id: string | number): Observable<SubmitResponse> {
    return this.patch(id, undefined, '', 'status') as unknown as Observable<SubmitResponse>;
  }

  // #region FAQ Categories
  getBlogsCategoriesList(
    page: number = 1,
    limit: number = 10,
    filters: { search?: string | null; isActive?: boolean | null } = {}
  ): Observable<BlogCategoryListResponse> {
    const hasSearch = !!filters.search;
    const hasStatus = filters.isActive !== null && filters.isActive !== undefined;

    const params: Record<string, string | number | boolean> = {};

    if (hasSearch) {
      params['search'] = filters.search as string;
    }
    if (hasStatus) {
      params['is_active'] = filters.isActive as boolean;
    }
    if (!hasSearch && !hasStatus) {
      params['page'] = page;
      params['limit'] = limit;
    }

    return this.getBy('categories', params) as unknown as Observable<BlogCategoryListResponse>;
  }

  getCategoryById(id: string | number): Observable<BlogCategory> {
    return this.getById(id, 'categories') as unknown as Observable<BlogCategory>;
  }

  addNewCategory(category: BlogCategoryPayload): Observable<BlogCategory> {
    return this.create(category, 'categories') as unknown as Observable<BlogCategory>;
  }

  updateCategory(id: string | number, category: BlogCategoryPayload): Observable<SubmitResponse> {
    return this.patch(id, category, 'categories') as unknown as Observable<SubmitResponse>;
  }

  deleteCategory(id: string | number): Observable<SubmitResponse> {
    return this.delete(id, 'categories') as unknown as Observable<SubmitResponse>;
  }

  updateCategoryStatus(id: string | number): Observable<SubmitResponse> {
    return this.patch(id, undefined, 'categories', 'status') as unknown as Observable<SubmitResponse>;
  }

  getFeaturedBlogs(): Observable<PublicBlog[]> {
    return this.getBy('public/featured') as unknown as Observable<PublicBlog[]>;
  }

  getBlogCategories(): Observable<PublicBlogCategory[]> {
    return this.getBy('public/categories') as unknown as Observable<PublicBlogCategory[]>;
  }

  getBlogById(id: string | number): Observable<Blog> {
    return this.getById(id, 'public') as unknown as Observable<Blog>;
  }

  /** Admin detail — full blog with content, SEO, and covers. */
  getAdminBlogById(id: string | number): Observable<Blog> {
    return this.getById(id) as unknown as Observable<Blog>;
  }

  getRelatedBlogs(id: string | number): Observable<PublicBlog[]> {
    return this.getBy(`public/${id}/related`) as unknown as Observable<PublicBlog[]>;
  }

  getPublicBlogs(
    query?: string,
    category_id?: string | number,
    category_type?: string | null,
    page: number = 1,
    limit: number = 12
  ): Observable<PublicBlogListResponse> {
    const params: Record<string, string | number> = {
      page,
      limit,
    };
    if (query) params['search'] = query;
    if (category_id !== undefined && category_id !== null && category_id !== '') {
      params['category_id'] = category_id;
      if (category_type) {
        params['category_type'] = category_type;
      }
    }
    return this.getBy('public', params) as unknown as Observable<PublicBlogListResponse>;
  }
  


}
